import { useState, useEffect } from 'react';
import { api } from '../utils/api';

const TEAM = ['Adam', 'Ray', 'Diana', 'Bob'];

// ── Bulletin Modal (unacknowledged bulletins, fires on load/focus) ─────────

export function BulletinModal({ user, onDone }) {
  const [bulletins, setBulletins] = useState([]);
  const [idx, setIdx] = useState(0);
  const [acking, setAcking] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const data = await api.getBulletins(user);
        setBulletins(data.bulletins || []);
      } catch (e) {}
      setLoaded(true);
    })();
  }, [user]);

  if (!loaded) return null;
  if (bulletins.length === 0) { onDone(); return null; }

  const current = bulletins[idx];
  const isUrgent = current?.priority === 'urgent';

  const ack = async () => {
    setAcking(true);
    try { await api.ackBulletin(current.id, user); } catch (e) {}
    setAcking(false);
    if (idx + 1 < bulletins.length) {
      setIdx(i => i + 1);
    } else {
      onDone();
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.6)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: '#fff', borderRadius: 14, maxWidth: 440, width: '100%', overflow: 'hidden', boxShadow: '0 8px 32px rgba(0,0,0,.25)' }}>
        {/* Header band */}
        <div style={{ background: isUrgent ? '#dc2626' : '#0f172a', color: '#fff', padding: '14px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {isUrgent && <span style={{ fontSize: 18 }}>🚨</span>}
            <span style={{ fontWeight: 700, fontSize: 15 }}>{isUrgent ? 'URGENT BULLETIN' : 'Bulletin'}</span>
          </div>
          {bulletins.length > 1 && (
            <span style={{ fontSize: 12, opacity: .7 }}>{idx + 1} of {bulletins.length}</span>
          )}
        </div>

        {/* Body */}
        <div style={{ padding: '20px 18px' }}>
          <div style={{ fontSize: 12, color: '#64748b', marginBottom: 8 }}>
            From <strong>{current.from_user}</strong> · {fmtTime(current.created_at)}
          </div>
          <div style={{ fontSize: 15, lineHeight: 1.6, color: '#0f172a', whiteSpace: 'pre-wrap', minHeight: 60 }}>
            {current.message}
          </div>
        </div>

        {/* Footer */}
        <div style={{ padding: '12px 18px 18px', display: 'flex', justifyContent: 'flex-end' }}>
          <button
            onClick={ack}
            disabled={acking}
            style={{ background: isUrgent ? '#dc2626' : '#3b82f6', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 24px', fontWeight: 700, fontSize: 14, cursor: 'pointer', opacity: acking ? .6 : 1 }}
          >
            {acking ? 'Acknowledging…' : idx + 1 < bulletins.length ? 'Acknowledge & Next' : 'Acknowledge'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Bulletin Board (manage / send bulletins) ──────────────────────────────

export function BulletinBoard({ user, onClose }) {
  const [bulletins, setBulletins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCompose, setShowCompose] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const data = await api.getAllBulletins();
      setBulletins(data.bulletins || []);
    } catch (e) {}
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const del = async (id) => {
    // eslint-disable-next-line no-restricted-globals
    if (!confirm('Delete this bulletin?')) return;
    await api.deleteBulletin(id);
    load();
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.55)', zIndex: 150, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '24px 12px', overflowY: 'auto' }}>
      <div style={{ background: '#fff', borderRadius: 14, width: '100%', maxWidth: 540, boxShadow: '0 8px 32px rgba(0,0,0,.2)' }}>
        {/* Header */}
        <div style={{ background: '#0f172a', color: '#fff', padding: '14px 18px', borderRadius: '14px 14px 0 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontWeight: 700, fontSize: 16 }}>📢 Bulletin Board</span>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: 20, lineHeight: 1 }}>×</button>
        </div>

        <div style={{ padding: 16 }}>
          <button
            onClick={() => setShowCompose(true)}
            style={{ background: '#3b82f6', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 18px', fontWeight: 600, fontSize: 14, cursor: 'pointer', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 6 }}
          >
            ✏️ Send Bulletin
          </button>

          {loading ? (
            <div style={{ textAlign: 'center', color: '#64748b', padding: 24 }}>Loading…</div>
          ) : bulletins.length === 0 ? (
            <div style={{ textAlign: 'center', color: '#94a3b8', padding: 24, fontSize: 14 }}>No bulletins sent yet.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {bulletins.map(b => (
                <div key={b.id} style={{ border: `1px solid ${b.priority === 'urgent' ? '#fca5a5' : '#e2e8f0'}`, borderRadius: 10, overflow: 'hidden' }}>
                  <div style={{ background: b.priority === 'urgent' ? '#fef2f2' : '#f8fafc', padding: '9px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid inherit' }}>
                    <div style={{ fontSize: 12, color: '#475569', display: 'flex', gap: 10, alignItems: 'center' }}>
                      {b.priority === 'urgent' && <span>🚨</span>}
                      <strong>{b.from_user}</strong>
                      <span>→ {fmtToUsers(b.to_users)}</span>
                      <span style={{ color: '#94a3b8' }}>{fmtTime(b.created_at)}</span>
                    </div>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <span style={{ fontSize: 11, color: '#64748b' }}>
                        {b.ack_count ?? 0} ack{b.ack_count !== 1 ? 's' : ''}
                      </span>
                      {b.from_user === user && (
                        <button onClick={() => del(b.id)} style={{ background: 'transparent', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: 13 }}>✕</button>
                      )}
                    </div>
                  </div>
                  <div style={{ padding: '10px 12px', fontSize: 14, color: '#1e293b', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
                    {b.message}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {showCompose && (
        <ComposeModal
          user={user}
          onSent={() => { setShowCompose(false); load(); }}
          onClose={() => setShowCompose(false)}
        />
      )}
    </div>
  );
}

// ── Compose Modal ─────────────────────────────────────────────────────────

function ComposeModal({ user, onSent, onClose }) {
  const [toAll, setToAll] = useState(true);
  const [toUsers, setToUsers] = useState([]);
  const [message, setMessage] = useState('');
  const [priority, setPriority] = useState('normal');
  const [sending, setSending] = useState(false);

  const togglePerson = (name) => {
    setToUsers(u => u.includes(name) ? u.filter(n => n !== name) : [...u, name]);
  };

  const send = async () => {
    if (!message.trim()) return;
    setSending(true);
    const id = `b_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
    const to = toAll ? 'ALL' : JSON.stringify(toUsers.length ? toUsers : ['ALL']);
    try {
      await api.saveBulletin({ id, from_user: user, to_users: to, message: message.trim(), priority });
      onSent();
    } catch (e) {
      alert('Failed to send bulletin.');
    }
    setSending(false);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.4)', zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: '#fff', borderRadius: 14, maxWidth: 440, width: '100%', boxShadow: '0 8px 32px rgba(0,0,0,.2)' }}>
        <div style={{ background: '#0f172a', color: '#fff', padding: '13px 18px', borderRadius: '14px 14px 0 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontWeight: 700, fontSize: 15 }}>Send Bulletin</span>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: 20 }}>×</button>
        </div>

        <div style={{ padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          {/* To */}
          <div>
            <div style={lbl}>To</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <span
                onClick={() => { setToAll(true); setToUsers([]); }}
                style={{ ...chipStyle, background: toAll ? '#0f172a' : '#f1f5f9', color: toAll ? '#fff' : '#334155' }}
              >Everyone</span>
              {TEAM.filter(n => n !== user).map(n => (
                <span
                  key={n}
                  onClick={() => { setToAll(false); togglePerson(n); }}
                  style={{ ...chipStyle, background: !toAll && toUsers.includes(n) ? '#3b82f6' : '#f1f5f9', color: !toAll && toUsers.includes(n) ? '#fff' : '#334155', cursor: 'pointer' }}
                >{n}</span>
              ))}
            </div>
          </div>

          {/* Priority */}
          <div>
            <div style={lbl}>Priority</div>
            <div style={{ display: 'flex', gap: 8 }}>
              {['normal', 'urgent'].map(p => (
                <span
                  key={p}
                  onClick={() => setPriority(p)}
                  style={{ ...chipStyle, background: priority === p ? (p === 'urgent' ? '#dc2626' : '#0f172a') : '#f1f5f9', color: priority === p ? '#fff' : '#334155', cursor: 'pointer' }}
                >{p === 'urgent' ? '🚨 Urgent' : '📢 Normal'}</span>
              ))}
            </div>
          </div>

          {/* Message */}
          <div>
            <div style={lbl}>Message</div>
            <textarea
              value={message}
              onChange={e => setMessage(e.target.value)}
              rows={4}
              style={{ width: '100%', border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 10px', fontSize: 14, resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit' }}
              placeholder="What does the team need to know?"
            />
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
            <button onClick={onClose} style={{ background: '#f1f5f9', border: 'none', borderRadius: 8, padding: '9px 18px', cursor: 'pointer', fontWeight: 600, fontSize: 14, color: '#334155' }}>Cancel</button>
            <button
              onClick={send}
              disabled={sending || !message.trim()}
              style={{ background: priority === 'urgent' ? '#dc2626' : '#3b82f6', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 18px', fontWeight: 700, fontSize: 14, cursor: 'pointer', opacity: (sending || !message.trim()) ? .6 : 1 }}
            >{sending ? 'Sending…' : 'Send'}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────

function fmtTime(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  if (isNaN(d)) return '';
  const now = new Date();
  const diff = (now - d) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

function fmtToUsers(to) {
  if (!to || to === 'ALL') return 'Everyone';
  try {
    const arr = JSON.parse(to);
    if (Array.isArray(arr)) return arr.join(', ');
  } catch (e) {}
  return to;
}

const lbl = { fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 6, textTransform: 'uppercase', letterSpacing: .5 };
const chipStyle = { display: 'inline-block', padding: '5px 12px', borderRadius: 20, fontSize: 13, fontWeight: 600, cursor: 'pointer', userSelect: 'none', transition: 'background .15s' };
